import { Box, Stack, TableCell, TableRow, Typography } from '@mui/material';
import { generateClient } from 'aws-amplify/api';
import { useEffect, useState } from 'react';
import { Schema } from '../../../../../amplify/data/resource';
import GenderIcon from '../../../../components/activeIcons/GenderIcon';
import { FundLogo } from '../../../../components/custom/FundLogo';
import {
  extractFundNumber,
  formatMoney,
  extractCompanyId,
  extractGeneralizedFundType,
} from '../../../../utils/format/format';
import { format } from 'date-fns';
import { he } from 'date-fns/locale';
import StatusTextCircle from '../../../../components/custom/StatusTextCircle';
import EmployeeSidebar from '../../File/Employees/EmployeeSidebar';

const apiClient = generateClient<Schema>();

// @ts-ignore
type Employee = Schema['Employee']['type'];

// @ts-ignore
type Deposit = Schema['Deposit']['type'];

interface RowProps {
  employee: Employee;
  currentPage?: number;
}

const EmployeeItem: React.FC<RowProps> = ({ employee, currentPage = 1 }) => {
  const totalContributionRate =
    employee.employeeContributionAllocation?.reduce(
      (sum: any, contribution: any) => {
        const rate = contribution?.contributionAmount;

        return sum + (typeof rate === 'number' ? rate : 0);
      },
      0,
    ) ?? 0;

  const [deposit, setDeposit] = useState<Deposit>();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const handleOpenEmployeeSidebar = () => {
    // Save current state before opening sidebar
    const containerElement = document.querySelector('[data-employees-container]') as HTMLElement;
    const scrollPosition = containerElement ? containerElement.scrollTop : window.scrollY;
    
    const state = {
      currentPage: currentPage,
      scrollPosition: scrollPosition,
      fileId: new URLSearchParams(window.location.search).get('fileId') || '',
    };
    sessionStorage.setItem('employees_page_state', JSON.stringify(state));
    setSidebarOpen(true);
  };

  const handleCloseEmployeeSidebar = () => {
    setSidebarOpen(false);
  };

  useEffect(() => {
    const fetchFundIdentifier = async () => {
      if (employee.depositId) {
        const getDepositResponse = await apiClient.models.Deposit.get({
          id: employee.depositId,
        });
        if (getDepositResponse.data) {
          setDeposit(getDepositResponse.data);
        }
      }
    };
    fetchFundIdentifier();
  }, [employee.id]);

  if (!employee.depositId) {
    return null;
  }

  return (
    <>
      <TableRow
        sx={{
          backgroundColor: '#F2F4F7',
          borderRadius: '10px',
          p: '20px',
          overflow: 'hidden',
          '&:last-child td, &:last-child th': { border: 0 },
          boxShadow: 'none',
          textAlign: 'right',
          cursor: 'pointer',
        }}
        onClick={handleOpenEmployeeSidebar}
      >
        <TableCell
          sx={{
            padding: '20px',
            borderBottom: 'none',
            borderTopRightRadius: '10px',
            borderBottomRightRadius: '10px',
          }}
        >
          <Box
            sx={{
              textAlign: 'right',
              display: 'flex',
              flexDirection: 'column',
              gap: '2px',
            }}
          >
            <Typography
              fontWeight={'700'}
              fontSize={'12px'}
              fontFamily={'Montserrat'}
            >
              {employee.date ? format(new Date(employee.date), 'dd/MM/yyyy') : ''}
            </Typography>
          </Box>
        </TableCell>

        <TableCell
          sx={{
            padding: '20px',
            borderBottom: 'none',
            borderTopRightRadius: '10px',
            borderBottomRightRadius: '10px',
          }}
        >
          <Box
            sx={{
              textAlign: 'right',
              display: 'flex',
              flexDirection: 'column',

              gap: '2px',
            }}
          >
            <Typography
              fontWeight={'700'}
              fontSize={'14px'}
              fontFamily={'AlmoniDL'}
            >
              {employee.salaryMonth
                ? format(new Date(employee.salaryMonth), 'LLLL yyyy', {
                    locale: he,
                  })
                : ''}
            </Typography>
          </Box>
        </TableCell>

        <TableCell
          sx={{
            padding: '20px',
            borderBottom: 'none',
          }}
        >
          <Box
            sx={{
              textAlign: 'right',
              display: 'flex',
              gap: '10px',
              alignItems: 'center',
            }}
          >
            <img src="/assets/employees/card.svg" />
            <Stack direction="row" gap={0.5}>
              <Typography
                dir="rtl"
                fontSize="14px"
                fontWeight="700"
                fontFamily={'AlmoniDL'}
              >
                {employee.firstName}
              </Typography>
              <Typography
                dir="rtl"
                fontSize="14px"
                fontWeight="700"
                fontFamily={'AlmoniDL'}
              >
                {employee.lastName}
              </Typography>
            </Stack>
          </Box>
        </TableCell>

        <TableCell
          sx={{
            padding: '20px',
            borderBottom: 'none',
          }}
        >
          <Box
            sx={{
              textAlign: 'right',
              display: 'flex',
              flexDirection: 'column',
              gap: '2px',
            }}
          >
            <Typography
              fontWeight={'700'}
              fontSize={'12px'}
              fontFamily={'Montserrat'}
            >
              {employee.employeeId}
            </Typography>
          </Box>
        </TableCell>
        <TableCell
          sx={{
            padding: '20px',
            borderBottom: 'none',
          }}
        >
          <Box
            sx={{
              textAlign: 'right',
              display: 'flex',
              flexDirection: 'column',

              gap: '2px',
            }}
          >
            <Box
              sx={{
                textAlign: 'right',
                display: 'flex',
                gap: '10px',
                alignItems: 'center',
              }}
            >
              <img src="/assets/employees/coins.svg" />
              <Typography
                fontWeight={'700'}
                fontSize={'12px'}
                fontFamily={'Montserrat'}
              >
                {formatMoney(totalContributionRate)}
              </Typography>
            </Box>
          </Box>
        </TableCell>
        <TableCell
          sx={{
            padding: '20px',
            borderBottom: 'none',
          }}
        >
          <Box
            sx={{
              textAlign: 'center',
              display: 'flex',
              flexDirection: 'column',

              gap: '2px',
            }}
          >
            <FundLogo
              width={'80px'}
              fundNumber={deposit?.fundIdentifier?.toString()}
            />
          </Box>
        </TableCell>
        <TableCell
          sx={{
            padding: '20px',
            borderBottom: 'none',
          }}
        >
          <Box
            sx={{
              textAlign: 'right',
              display: 'flex',
              flexDirection: 'column',

              gap: '2px',
            }}
          >
            <Typography
              fontWeight={'700'}
              fontSize={'12px'}
              fontFamily={'AlmoniDL'}
            >
              {extractGeneralizedFundType(
                deposit?.fundType ?? '',
                deposit?.fundIdentifier ?? '',
              )}
            </Typography>
          </Box>
        </TableCell>
        <TableCell
          sx={{
            padding: '20px',
            borderBottom: 'none',
          }}
        >
          <Box
            sx={{
              textAlign: 'right',
              display: 'flex',
              flexDirection: 'column',

              gap: '2px',
            }}
          >
            <Typography
              fontWeight={'700'}
              fontSize={'12px'}
              fontFamily={'Montserrat'}
            >
              {(() => {
                const fundNumber = extractFundNumber(deposit?.fundIdentifier);
                return fundNumber === 0
                  ? extractCompanyId(deposit?.fundIdentifier)
                  : fundNumber.toString();
              })()}
            </Typography>
          </Box>
        </TableCell>
        <TableCell
          sx={{
            padding: '20px',
            borderBottom: 'none',
            borderTopLeftRadius: '10px',
            borderBottomLeftRadius: '10px',
          }}
        >
          <Box
            sx={{
              textAlign: 'right',
              display: 'flex',
              flexDirection: 'column',

              gap: '2px',
            }}
          >
            <StatusTextCircle status={
    employee.status === 'RECORD_CAPTURED_SUCCESS' 
      ? 'COMPLETED' 
      : employee.status === 'PENDING' 
        ? 'PENDING' 
        : 'IN_PROGRESS'
  } />
          </Box>
        </TableCell>
      </TableRow>

      <EmployeeSidebar
        open={sidebarOpen}
        onClose={handleCloseEmployeeSidebar}
        employeeId={employee.employeeId || ''}
      />
    </>
  );
};

export default EmployeeItem;
